import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Pill, ArrowLeft, ArrowRight, CheckCircle, Eye, X, Search, Check, Calculator, Info } from "lucide-react";
import { toast } from "sonner";
import HeightPicker from "@/components/questionnaire/HeightPicker";
import DrugSearchInline from "@/components/questionnaire/DrugSearchInline";
import BmiInfoModal from "@/components/BmiInfoModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function MedicalQuestionnaire() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get("from") === "onboarding";
  const [step, setStep] = useState(0);
  // Draft autosave — every keystroke persists into localStorage so the
  // patient can bail out mid-intake and come back to the exact same
  // page without losing anything. Cleared on successful submit. Keyed
  // per-user via the stashed patient id on the token so switching
  // accounts on the same device doesn't cross-contaminate drafts.
  const DRAFT_KEY = "glpflo:questionnaire-draft:v1";
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [existingQ, setExistingQ] = useState(null);
  const [questionnaireId, setQuestionnaireId] = useState(null);
  const [profileMeds, setProfileMeds] = useState([]);
  const [profileAllergies, setProfileAllergies] = useState("");
  const [profileNkda, setProfileNkda] = useState(false);
  const [qMeds, setQMeds] = useState([]);
  const [qNkda, setQNkda] = useState(false);

  // BMI mini-calculator
  const [bmiOpen, setBmiOpen] = useState(false);
  const [bmiInfoOpen, setBmiInfoOpen] = useState(false);
  const [bmiFeet, setBmiFeet] = useState("");
  const [bmiInches, setBmiInches] = useState("");
  const [bmiWeight, setBmiWeight] = useState("");

  const bmiComputed = (() => {
    const ft = parseInt(bmiFeet, 10);
    const inches = parseInt(bmiInches || "0", 10);
    const lbs = parseFloat(bmiWeight);
    if (!ft || !lbs || ft < 3 || ft > 8 || lbs < 60 || lbs > 800) return null;
    const totalInches = ft * 12 + (isFinite(inches) ? inches : 0);
    if (totalInches <= 0) return null;
    // BMI = (lbs / inches^2) * 703
    return Math.round(((lbs / (totalInches * totalInches)) * 703) * 10) / 10;
  })();

  const bmiBucket = (bmi) => {
    if (bmi == null) return null;
    if (bmi >= 30) return "BMI 30+";
    if (bmi >= 27) return "BMI 27-29.9 with comorbidity";
    return "Below 27";
  };

  const applyBmiAnswer = () => {
    if (bmiComputed == null) return;
    const bucket = bmiBucket(bmiComputed);
    updateAnswer("bmi_over_27", bucket);
    // If user hasn't filled height/weight on step 0 yet, pre-fill from calculator.
    if (!answers.height_feet) updateAnswer("height_feet", bmiFeet);
    if (!answers.height_inches && bmiInches) updateAnswer("height_inches", bmiInches);
    if (!answers.weight) updateAnswer("weight", bmiWeight);
    setBmiOpen(false);
    toast.success(`Your BMI is ${bmiComputed} — answer set to "${bucket}".`);
  };

  // Pre-seed calculator from any values the patient already entered on step 0.
  useEffect(() => {
    if (!bmiOpen) return;
    if (!bmiFeet && answers.height_feet) setBmiFeet(String(answers.height_feet));
    if (!bmiInches && answers.height_inches) setBmiInches(String(answers.height_inches));
    if (!bmiWeight && answers.weight) setBmiWeight(String(answers.weight));
    // only at open; don't overwrite while the user types in the modal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bmiOpen]);

  // Fields expected on each step — used by the "skip answered" jump so
  // returning patients land on the first blank question instead of
  // scrolling past everything they already filled out. Keep in sync
  // with the actual radio/input fields rendered in each step below.
  const STEP_KEYS = [
    // Step 0 — Personal info
    ["height_feet", "height_inches", "weight", "goal_weight"],
    // Step 1 — Medical history + BMI
    [
      "diabetes", "thyroid", "pancreatitis", "kidney_disease", "heart_disease",
      "gallbladder", "eating_disorder", "sleep_apnea", "depression_anxiety",
      "pcos", "pregnant_nursing", "gastroparesis", "bmi_over_27",
    ],
    // Step 2 — Medications & allergies (previous_glp1 gates sub-fields;
    // meds + allergies live outside `answers` so we treat them optional)
    ["previous_glp1"],
    // Step 3 — Lifestyle
    ["exercise_frequency", "diet_type", "primary_goal", "weight_loss_tried", "commitment_level"],
  ];
  function firstIncompleteStep(a) {
    for (let s = 0; s < STEP_KEYS.length; s++) {
      for (const key of STEP_KEYS[s]) {
        const v = a?.[key];
        const isEmpty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
        if (isEmpty) return { step: s, key };
      }
    }
    return null;
  }

  const loadExisting = useCallback(async () => {
    try {
      // Load profile data for meds/allergies auto-pull
      const profileRes = await API.get("/profile");
      const p = profileRes.data;
      setProfileMeds(p.current_medications_list || []);
      setProfileAllergies(p.allergies || "");
      setProfileNkda(p.allergies_nkda || p.allergies === "NKDA");

      // Check for existing questionnaire
      const { data: qs } = await API.get("/questionnaires/mine");
      const wantsEdit = searchParams.get("edit") === "1";
      if (qs.length > 0) {
        const latest = qs[0];
        setExistingQ(latest);
        setAnswers(latest.answers || {});
        setQuestionnaireId(latest.id);
        // Restore meds/allergies from saved answers
        setQMeds(latest.answers?.medications_list || p.current_medications_list || []);
        setQNkda(latest.answers?.allergies_nkda || p.allergies_nkda || false);
        if (wantsEdit) {
          // Jump straight to the first blank question so returning
          // patients don't re-scroll past filled answers.
          setViewOnly(false);
          const target = firstIncompleteStep(latest.answers || {});
          if (target) {
            setStep(target.step);
            requestAnimationFrame(() => {
              const el = document.querySelector(`[data-testid="q-${target.key}"]`);
              el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
            });
          }
        } else {
          setViewOnly(true);
        }
      } else {
        // Pre-fill from profile
        setQMeds(p.current_medications_list || []);
        setQNkda(p.allergies_nkda || p.allergies === "NKDA");
        // Rehydrate any autosaved draft so a patient who bailed
        // mid-intake picks up exactly where they left off. Silently
        // ignore parse errors — a bad draft is just discarded.
        try {
          const raw = typeof window !== "undefined" ? localStorage.getItem(DRAFT_KEY) : null;
          if (raw) {
            const d = JSON.parse(raw);
            if (d && typeof d === "object") {
              if (d.answers && typeof d.answers === "object") setAnswers(d.answers);
              if (Array.isArray(d.qMeds)) setQMeds(d.qMeds);
              if (typeof d.qNkda === "boolean") setQNkda(d.qNkda);
              if (Number.isInteger(d.step)) setStep(Math.min(STEP_KEYS.length - 1, Math.max(0, d.step)));
              // Jump straight to their first blank so they don't
              // re-scroll past already-answered questions.
              const target = firstIncompleteStep(d.answers || {});
              if (target) {
                setStep(target.step);
                requestAnimationFrame(() => {
                  const el = document.querySelector(`[data-testid="q-${target.key}"]`);
                  el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
                });
              }
            }
          }
        } catch { /* invalid JSON — ignore */ }
      }
    } catch (e) { console.error(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  // Persist every answer change into localStorage so a patient can
  // leave and pick up right where they left off. Only writes while in
  // fill mode (skip persistence in view-only). Small enough (<3KB
  // typical) not to worry about storage quota.
  useEffect(() => {
    if (typeof window === "undefined" || viewOnly) return;
    if (!answers || Object.keys(answers).length === 0) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        answers,
        qMeds,
        qNkda,
        step,
        savedAt: Date.now(),
      }));
    } catch { /* quota or private-mode — ignore */ }
  }, [answers, qMeds, qNkda, step, viewOnly]);

  const updateAnswer = (key, value) => setAnswers({ ...answers, [key]: value });

  // Auto-advance helper — only fires from radio-style selections so text
  // inputs don't jump the page on every keystroke. Waits one paint tick
  // so React's re-render lands before we search for the next field, then
  // scrolls the next `[data-testid^="q-"]` container into view. Long GLP-1
  // medical-history step in particular is easy to miss questions on.
  const advanceToNext = (key) => {
    if (typeof window === "undefined") return;
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="q-${key}"]`);
      if (!el) return;
      // Each radio field is wrapped in a `<div key={field.key}>` sitting
      // directly under the step's `space-y-*` list. Walk up to that
      // wrapper, then jump to the next sibling if it holds another
      // question (any descendant with `data-testid^="q-"`).
      let wrapper = el.parentElement;
      while (wrapper && wrapper.parentElement && !wrapper.parentElement.className?.includes("space-y")) {
        wrapper = wrapper.parentElement;
      }
      const sib = wrapper?.nextElementSibling;
      if (sib && typeof sib.scrollIntoView === "function") {
        sib.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };
  const updateAnswerAndAdvance = (key, value) => {
    updateAnswer(key, value);
    if (value) advanceToNext(key);
  };

  const STEPS = [
    { id: "personal", title: "Personal Information" },
    { id: "medical_history", title: "Medical History" },
    { id: "medications", title: "Current Medications & Allergies" },
    { id: "lifestyle", title: "Lifestyle & Goals" },
  ];

  // Show previous GLP-1 sub-step if answered yes
  const showGlpFollowup = answers.previous_glp1 === "Yes";

  const progress = ((step + 1) / STEPS.length) * 100;

  const handleSubmit = async () => {
    // Missed-question guard — walk the same STEP_KEYS map used by
    // "Skip Answered" and if anything is blank, jump the patient back
    // to that field with a soft toast instead of silently submitting an
    // incomplete intake. Meds / allergies / previous_glp sub-fields are
    // handled downstream by the N/A / NKDA fallbacks, so this only
    // guards the primary radio / input answers.
    const missing = firstIncompleteStep(answers);
    if (missing) {
      const label = missing.key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      toast.error(`One quick blank — "${label}" is still empty.`);
      setStep(missing.step);
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-testid="q-${missing.key}"]`);
        el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      });
      return;
    }
    setSubmitting(true);
    try {
      const submitData = {
        ...answers,
        medications_list: qMeds,
        current_medications: qMeds.join(", ") || "None",
        allergies: qNkda ? "NKDA" : (answers.allergies || profileAllergies || "None"),
        allergies_nkda: qNkda,
        // Auto-fill N/A for previous GLP-1 follow-ups if blank
        previous_glp_name: answers.previous_glp_name?.trim() || "N/A",
        previous_glp_last_time: answers.previous_glp_last_time?.trim() || "N/A",
        previous_glp_last_dose: answers.previous_glp_last_dose?.trim() || "N/A",
        previous_glp_symptoms: answers.previous_glp_symptoms?.trim() || "N/A",
      };
      const { data } = await API.post("/questionnaires", { answers: submitData });
      // Draft is now committed — clear the local autosave so returning
      // to the page in future doesn't re-hydrate stale keystrokes over
      // the persisted questionnaire.
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      try {
        const { analytics } = await import("@/instrument");
        analytics.capture("questionnaire_completed", {});
      } catch (_e) { /* noop */ }
      setQuestionnaireId(data.id);
      setViewOnly(true);
      setExistingQ(data);
      toast.success("Questionnaire submitted successfully!");
      // Always drop the patient into the medication picker so they can
      // finish the flow without hunting for the next step. Previously
      // this only fired on ?from=onboarding — patients completing the
      // form from the dashboard just stayed on the completed screen at
      // the bottom of the page.
      navigate("/medications", { state: { questionnaireId: data.id } });
    } catch (e) {
      toast.error("Failed to submit questionnaire");
    } finally { setSubmitting(false); }
  };

  // View-only mode for already submitted questionnaires
  if (viewOnly && existingQ) {
    const a = existingQ.answers || {};
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100" data-testid="questionnaire-view-only">
        <div className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
              <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
            </Link>
            <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-semibold">
              <Eye className="w-3.5 h-3.5" /> View Only — Questionnaire Submitted
            </div>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
          <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center"><CheckCircle className="w-6 h-6 text-emerald-600" /></div>
              <div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Questionnaire Complete</h2>
                <p className="text-sm text-slate-500">Submitted on {new Date(existingQ.submitted_at).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="space-y-6 divide-y divide-slate-100">
              {/* Personal Info */}
              <div className="pt-4">
                <h3 className="text-sm font-bold text-purple-600 uppercase tracking-wider mb-3">Personal Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div><p className="text-xs text-slate-400">Height</p><p className="text-sm font-medium text-slate-800">{a.height_feet || "—"}ft {a.height_inches || "0"}in</p></div>
                  <div><p className="text-xs text-slate-400">Weight</p><p className="text-sm font-medium text-slate-800">{a.weight || "—"} lbs</p></div>
                  <div><p className="text-xs text-slate-400">Goal Weight</p><p className="text-sm font-medium text-slate-800">{a.goal_weight || "—"} lbs</p></div>
                </div>
              </div>
              {/* Medical History */}
              <div className="pt-4">
                <h3 className="text-sm font-bold text-purple-600 uppercase tracking-wider mb-3">Medical History</h3>
                <div className="grid grid-cols-2 gap-3">
                  {["diabetes","thyroid","pancreatitis","kidney_disease","heart_disease","gallbladder","eating_disorder","sleep_apnea","depression_anxiety","pcos"].map(k => (
                    a[k] ? <div key={k}><p className="text-xs text-slate-400">{k.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}</p><p className="text-sm font-medium text-slate-800">{a[k]}</p></div> : null
                  ))}
                </div>
              </div>
              {/* Medications */}
              <div className="pt-4">
                <h3 className="text-sm font-bold text-purple-600 uppercase tracking-wider mb-3">Medications & Allergies</h3>
                <p className="text-xs text-slate-400">Current Medications</p>
                <p className="text-sm font-medium text-slate-800 mb-2">{a.current_medications || (a.medications_list || []).join(", ") || "None"}</p>
                <p className="text-xs text-slate-400">Allergies</p>
                <p className="text-sm font-medium text-slate-800 mb-2">{a.allergies || "None"}</p>
                <p className="text-xs text-slate-400">Previous GLP-1 Use</p>
                <p className="text-sm font-medium text-slate-800">{a.previous_glp1 || "—"}</p>
                {a.previous_glp1 === "Yes" && (
                  <div className="mt-2 bg-purple-50 rounded-xl p-3 space-y-1">
                    <p className="text-xs"><span className="text-slate-400">Previous GLP-1:</span> <span className="font-medium text-slate-800">{a.previous_glp_name || "N/A"}</span></p>
                    <p className="text-xs"><span className="text-slate-400">Last taken:</span> <span className="font-medium text-slate-800">{a.previous_glp_last_time || "N/A"}</span></p>
                    <p className="text-xs"><span className="text-slate-400">Last dose:</span> <span className="font-medium text-slate-800">{a.previous_glp_last_dose || "N/A"}</span></p>
                    <p className="text-xs"><span className="text-slate-400">Symptoms:</span> <span className="font-medium text-slate-800">{a.previous_glp_symptoms || "N/A"}</span></p>
                  </div>
                )}
              </div>
              {/* Lifestyle */}
              <div className="pt-4">
                <h3 className="text-sm font-bold text-purple-600 uppercase tracking-wider mb-3">Lifestyle & Goals</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-slate-400">Exercise</p><p className="text-sm font-medium text-slate-800">{a.exercise_frequency || "—"}</p></div>
                  <div><p className="text-xs text-slate-400">Diet</p><p className="text-sm font-medium text-slate-800">{a.diet_type || "—"}</p></div>
                  <div><p className="text-xs text-slate-400">Goal</p><p className="text-sm font-medium text-slate-800">{a.primary_goal || "—"}</p></div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100 flex gap-3 justify-end flex-wrap">
              <Button variant="ghost" className="rounded-full text-slate-600" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
              </Button>
              <Button
                variant="outline"
                className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={() => {
                  // Kick the patient into edit mode; loadExisting reads
                  // `?edit=1` and jumps to the first blank question via
                  // firstIncompleteStep so they don't re-scroll past
                  // filled answers.
                  navigate("/questionnaire?edit=1");
                  setViewOnly(false);
                  const target = firstIncompleteStep(existingQ.answers || {});
                  if (target) {
                    setStep(target.step);
                    requestAnimationFrame(() => {
                      const el = document.querySelector(`[data-testid="q-${target.key}"]`);
                      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
                    });
                  } else {
                    setStep(0);
                  }
                }}
                data-testid="update-questionnaire-btn"
              >
                Update answers
              </Button>
              <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-8" onClick={() => navigate("/medications")} data-testid="go-to-medications-btn">
                Select Medication <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- STEP RENDERERS ----
  const renderStep = () => {
    switch (step) {
      case 0: // Personal Information
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Height (feet & inches)</Label>
              <HeightPicker
                feet={answers.height_feet || ""}
                inches={answers.height_inches || ""}
                onFeetChange={(v) => updateAnswer("height_feet", v)}
                onInchesChange={(v) => updateAnswer("height_inches", v)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Current Weight (lbs)</Label>
              <Input type="number" value={answers.weight || ""} onChange={(e) => updateAnswer("weight", e.target.value)} placeholder="e.g. 185" className="rounded-xl" data-testid="q-weight" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Goal Weight (lbs)</Label>
              <Input type="number" value={answers.goal_weight || ""} onChange={(e) => updateAnswer("goal_weight", e.target.value)} placeholder="e.g. 160" className="rounded-xl" data-testid="q-goal-weight" />
            </div>
          </div>
        );

      case 1: // Medical History - GLP-1 specific
        return (
          <div className="space-y-5">
            {[
              { key: "diabetes", label: "Do you have diabetes?", options: ["Yes - Type 1", "Yes - Type 2", "No", "Pre-diabetic"] },
              { key: "thyroid", label: "History of thyroid disease, thyroid cancer, or MEN2 syndrome?", options: ["Yes", "No", "Not sure"] },
              { key: "pancreatitis", label: "History of pancreatitis or gallbladder disease?", options: ["Yes", "No"] },
              { key: "kidney_disease", label: "History of kidney disease or impaired kidney function?", options: ["Yes", "No"] },
              { key: "heart_disease", label: "History of heart disease, stroke, or cardiovascular events?", options: ["Yes", "No"] },
              { key: "gallbladder", label: "Have you had your gallbladder removed?", options: ["Yes", "No"] },
              { key: "eating_disorder", label: "History of eating disorders (anorexia, bulimia)?", options: ["Yes", "No", "Prefer not to say"] },
              { key: "sleep_apnea", label: "Do you have sleep apnea?", options: ["Yes - Treated", "Yes - Untreated", "No"] },
              { key: "depression_anxiety", label: "Do you currently experience depression or anxiety?", options: ["Yes - Treated", "Yes - Untreated", "No"] },
              { key: "pcos", label: "Do you have PCOS (Polycystic Ovary Syndrome)?", options: ["Yes", "No", "N/A"] },
              { key: "pregnant_nursing", label: "Are you currently pregnant, breastfeeding, or planning to become pregnant?", options: ["Yes", "No", "N/A"] },
              { key: "gastroparesis", label: "Do you have gastroparesis or severe GI motility issues?", options: ["Yes", "No"] },
            ].map((field) => (
              <div key={field.key}>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">{field.label}</Label>
                <RadioGroup value={answers[field.key] || ""} onValueChange={(val) => updateAnswerAndAdvance(field.key, val)} className="space-y-2" data-testid={`q-${field.key}`}>
                  {field.options.map((opt) => (
                    <label key={opt} className="flex items-center gap-3 bg-slate-50 hover:bg-purple-50 border border-slate-200 hover:border-purple-200 rounded-xl px-4 py-3 cursor-pointer transition-all">
                      <RadioGroupItem value={opt} />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            ))}

            {/* BMI — separate question with mini-calculator helper */}
            <div>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="text-sm font-medium text-slate-700 block">Is your BMI 27 or above, or 30 or above?</Label>
                  <button
                    type="button"
                    onClick={() => setBmiInfoOpen(true)}
                    className="text-xs font-medium text-slate-500 hover:text-purple-700 inline-flex items-center gap-1"
                    data-testid="bmi-question-info-open"
                  >
                    <Info className="w-3.5 h-3.5" /> What is BMI?
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setBmiOpen(true)}
                  className="text-xs font-medium text-purple-600 hover:text-purple-700 inline-flex items-center gap-1"
                  data-testid="bmi-calculator-open"
                >
                  <Calculator className="w-3.5 h-3.5" /> Calculate my BMI
                </button>
              </div>
              <RadioGroup value={answers.bmi_over_27 || ""} onValueChange={(val) => updateAnswerAndAdvance("bmi_over_27", val)} className="space-y-2" data-testid="q-bmi_over_27">
                {["BMI 27-29.9 with comorbidity", "BMI 30+", "Below 27", "Not sure"].map((opt) => (
                  <label key={opt} className="flex items-center gap-3 bg-slate-50 hover:bg-purple-50 border border-slate-200 hover:border-purple-200 rounded-xl px-4 py-3 cursor-pointer transition-all">
                    <RadioGroupItem value={opt} />
                    <span className="text-sm text-slate-700">{opt}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
        );

      case 2: // Current Medications & Allergies (auto-pulled from profile)
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-700">
              Medications and allergies have been pre-filled from your profile. You can update them here if needed.
            </div>

            {/* Current Medications */}
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Current Medications</Label>
              <DrugSearchInline medications={qMeds} setMedications={setQMeds} />
            </div>

            {/* Allergies with NKDA */}
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Known Allergies</Label>
              <div className="flex items-center gap-3 mb-2">
                <Checkbox id="q-nkda" checked={qNkda} onCheckedChange={(checked) => { setQNkda(checked); if (checked) updateAnswer("allergies", "NKDA"); }}
                  className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="q-nkda-checkbox" />
                <label htmlFor="q-nkda" className="text-sm text-slate-600 cursor-pointer flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-600" /> NKDA — No Known Drug Allergies
                </label>
              </div>
              {!qNkda && (
                <Textarea value={answers.allergies || ""} onChange={(e) => updateAnswer("allergies", e.target.value)}
                  placeholder="List drug or food allergies..." className="rounded-xl min-h-[80px]" data-testid="q-allergies" />
              )}
              {qNkda && <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700 font-medium">NKDA — No Known Drug Allergies</div>}
            </div>

            {/* Previous GLP-1 */}
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Have you taken GLP-1 medications before?</Label>
              <RadioGroup value={answers.previous_glp1 || ""} onValueChange={(val) => updateAnswer("previous_glp1", val)} className="space-y-2" data-testid="q-previous-glp1">
                {["Yes", "No"].map((opt) => (
                  <label key={opt} className="flex items-center gap-3 bg-slate-50 hover:bg-purple-50 border border-slate-200 hover:border-purple-200 rounded-xl px-4 py-3 cursor-pointer transition-all">
                    <RadioGroupItem value={opt} />
                    <span className="text-sm text-slate-700">{opt}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* GLP-1 Follow-up questions if Yes */}
            {showGlpFollowup && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 space-y-4" data-testid="glp1-followup">
                <h4 className="text-sm font-bold text-purple-700">Previous GLP-1 Details</h4>
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 leading-relaxed" data-testid="glp1-2week-rule-note">
                  <strong>Clinical safety rule:</strong> if your last GLP-1 dose was
                  <strong> more than 2 weeks ago</strong>, your tolerance has reset
                  and you&apos;ll restart at the <strong>Low dose</strong>. If your last
                  dose was within 2 weeks, your provider will continue you at your
                  attested previous dose — please be accurate below.
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">1. What GLP-1 did you previously take?</Label>
                  <Input value={answers.previous_glp_name || ""} onChange={(e) => updateAnswer("previous_glp_name", e.target.value)}
                    placeholder="e.g. Semaglutide, Tirzepatide, Ozempic" className="mt-1 rounded-xl" data-testid="q-prev-glp-name" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">2. When was the last time you took this GLP-1?</Label>
                  <Input value={answers.previous_glp_last_time || ""} onChange={(e) => updateAnswer("previous_glp_last_time", e.target.value)}
                    placeholder="e.g. 2 months ago, January 2026" className="mt-1 rounded-xl" data-testid="q-prev-glp-time" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">3. What was the last dose you took? <span className="font-normal text-slate-400">(Auto-fills N/A if blank)</span></Label>
                  <Input value={answers.previous_glp_last_dose || ""} onChange={(e) => updateAnswer("previous_glp_last_dose", e.target.value)}
                    placeholder="e.g. 0.5mg, 2.5mg" className="mt-1 rounded-xl" data-testid="q-prev-glp-dose" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">4. Did you have any symptoms on previous GLP-1?</Label>
                  <Textarea value={answers.previous_glp_symptoms || ""} onChange={(e) => updateAnswer("previous_glp_symptoms", e.target.value)}
                    placeholder="e.g. Nausea, fatigue, injection site reactions..." className="mt-1 rounded-xl min-h-[80px]" data-testid="q-prev-glp-symptoms" />
                </div>
              </div>
            )}
          </div>
        );

      case 3: // Lifestyle
        return (
          <div className="space-y-5">
            {[
              { key: "exercise_frequency", label: "How often do you exercise?", options: ["Daily", "3-5 times/week", "1-2 times/week", "Rarely", "Never"] },
              { key: "diet_type", label: "Describe your current diet", options: ["Balanced/Healthy", "Mostly healthy", "Average", "Could improve", "Poor"] },
              { key: "primary_goal", label: "Primary health goal", options: ["Weight loss", "Blood sugar management", "Overall wellness", "Reduce medications", "Improve energy"] },
              { key: "commitment_level", label: "How committed are you to lifestyle changes alongside medication?", options: ["Very committed", "Somewhat committed", "Will try my best", "Medication only for now"] },
            ].map((field) => (
              <div key={field.key}>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">{field.label}</Label>
                <RadioGroup value={answers[field.key] || ""} onValueChange={(val) => updateAnswerAndAdvance(field.key, val)} className="space-y-2" data-testid={`q-${field.key}`}>
                  {field.options.map((opt) => (
                    <label key={opt} className="flex items-center gap-3 bg-slate-50 hover:bg-purple-50 border border-slate-200 hover:border-purple-200 rounded-xl px-4 py-3 cursor-pointer transition-all">
                      <RadioGroupItem value={opt} />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            ))}

            {/* Weight loss methods — MULTI-SELECT (check all that apply). */}
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1 block">What weight loss methods have you tried before?</Label>
              <p className="text-xs text-slate-500 mb-2">Select all that apply.</p>
              <div className="space-y-2" data-testid="q-weight_loss_tried">
                {["Diet & exercise only", "Prescription medications", "OTC supplements", "Surgery", "None"].map((opt) => {
                  const current = Array.isArray(answers.weight_loss_tried) ? answers.weight_loss_tried : [];
                  const checked = current.includes(opt);
                  const toggle = () => {
                    // "None" is exclusive — picking it clears the rest, and picking anything else clears "None".
                    let next;
                    if (opt === "None") {
                      next = checked ? [] : ["None"];
                    } else {
                      next = checked ? current.filter((x) => x !== opt) : [...current.filter((x) => x !== "None"), opt];
                    }
                    updateAnswer("weight_loss_tried", next);
                  };
                  return (
                    <label key={opt} className={`flex items-center gap-3 border rounded-xl px-4 py-3 cursor-pointer transition-all ${checked ? "bg-purple-50 border-purple-300" : "bg-slate-50 hover:bg-purple-50 border-slate-200 hover:border-purple-200"}`}>
                      <Checkbox checked={checked} onCheckedChange={toggle} className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600" data-testid={`q-weight_loss_tried-${opt.replace(/\W+/g, '-').toLowerCase()}`} />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100" data-testid="medical-questionnaire-page">
      {/* Sticky header now bundles the step counter AND the progress bar
          so patients can always see how much of the intake is left. */}
      <div className="bg-white/90 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50 shadow-[0_2px_8px_rgb(0,0,0,0.03)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
          <span className="text-xs sm:text-sm font-medium text-slate-600" data-testid="questionnaire-step-count">Step {step + 1} of {STEPS.length}</span>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-3">
          <Progress value={progress} className="h-2" data-testid="questionnaire-progress" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
        <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 lg:p-10">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {STEPS[step].title}
          </h2>

          {renderStep()}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={step === 0} className="rounded-full text-slate-600" data-testid="questionnaire-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep(step + 1)} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-8" data-testid="questionnaire-next-btn">
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-8" data-testid="questionnaire-submit-btn">
                {submitting ? "Submitting..." : "Submit Questionnaire"} {!submitting && <CheckCircle className="w-4 h-4 ml-2" />}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* BMI mini-calculator dialog */}
      <Dialog open={bmiOpen} onOpenChange={setBmiOpen}>
        <DialogContent className="max-w-sm" data-testid="bmi-calculator-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Quick BMI Calculator</DialogTitle>
          </DialogHeader>          <div className="space-y-4">
            <p className="text-xs text-slate-500">Enter your height and weight — we&apos;ll compute your BMI and fill in the answer for you.</p>
            <div>
              <Label className="text-xs font-medium text-slate-600">Height (feet &amp; inches)</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input type="number" value={bmiFeet} onChange={(e) => setBmiFeet(e.target.value)} placeholder="Feet (e.g. 5)" min={3} max={8} className="rounded-xl" data-testid="bmi-feet" />
                <Input type="number" value={bmiInches} onChange={(e) => setBmiInches(e.target.value)} placeholder="Inches (e.g. 8)" min={0} max={11} className="rounded-xl" data-testid="bmi-inches" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Weight (lbs)</Label>
              <Input type="number" value={bmiWeight} onChange={(e) => setBmiWeight(e.target.value)} placeholder="e.g. 185" min={60} max={800} className="mt-1 rounded-xl" data-testid="bmi-weight" />
            </div>
            <div className={`rounded-xl p-4 text-center ${bmiComputed == null ? "bg-slate-50 text-slate-400" : bmiComputed >= 30 ? "bg-emerald-50 text-emerald-700" : bmiComputed >= 27 ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-700"}`} data-testid="bmi-result">
              {bmiComputed == null ? (
                <span className="text-xs">Fill in height and weight to see your BMI.</span>
              ) : (
                <>
                  <p className="text-[11px] uppercase tracking-wider">Your BMI</p>
                  <p className="text-3xl font-black">{bmiComputed}</p>
                  <p className="text-xs mt-1">We&apos;ll set your answer to <b>{bmiBucket(bmiComputed)}</b>.</p>
                </>
              )}
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">BMI is a screening tool, not a diagnosis. Your prescriber will confirm eligibility based on your full health history.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBmiOpen(false)}>Cancel</Button>
            <Button onClick={applyBmiAnswer} disabled={bmiComputed == null} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="bmi-apply">Use this answer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "What is BMI?" educational modal — shared with the landing page */}
      <BmiInfoModal open={bmiInfoOpen} onClose={() => setBmiInfoOpen(false)} />
    </div>
  );
}
